import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDownIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import { useParams } from "react-router-dom";

import {
  PastRunsSidebar,
  ReportTitle,
  RunLoading,
  RunProgress,
  TOOL_DOMAIN_FINDER,
  ToolTwoColumn,
  availabilityLabel,
  downloadCsv,
  truncateText,
  useToolRun,
} from "./shared";

const EXTENSIONS = [".com", ".co", ".io", ".ai", ".net", ".org", ".app", ".site"];
const EXTENSION_RE = /^\.[a-z0-9]([a-z0-9-]{0,22}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,22}[a-z0-9])?)*$/;

const normalizeExt = (raw) => {
  let value = (raw || "").trim().toLowerCase();
  if (!value) return null;
  if (!value.startsWith(".")) value = `.${value}`;
  if (!EXTENSION_RE.test(value)) return null;
  return value;
};

const STYLES = [
  { id: "exact", label: "Exact / keyword" },
  { id: "brandable", label: "Brandable" },
  { id: "short", label: "Short (≤10)" },
  { id: "two_word", label: "Two-word" },
  { id: "includes_keyword", label: "Includes keyword" },
];

const EMPTY_FILTERS = {
  extension: "",
  style: "",
  flavor: "",
  maxPrice: "",
  hidePremium: false,
};

const CSV_COLUMNS = [
  { label: "domain", value: (r) => r.domain },
  { label: "score", value: (r) => r.score },
  { label: "style", value: (r) => r.style },
  { label: "flavor", value: (r) => r.flavor },
  { label: "why", value: (r) => r.why },
  { label: "price", value: (r) => r.price },
  { label: "register_url", value: (r) => r.register_url },
];

const SortHeader = ({ label, column, sortKey, sortDir, onSort, className = "" }) => {
  const active = sortKey === column;
  return (
    <th className={`px-3 py-2.5 font-medium ${className}`}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wide transition ${
          active ? "text-theme-text opacity-90" : "text-theme-text opacity-45 hover:opacity-70"
        }`}
      >
        {label}
        {active ? (
          sortDir === "asc" ? (
            <ArrowUpIcon className="h-3 w-3" />
          ) : (
            <ArrowDownIcon className="h-3 w-3" />
          )
        ) : null}
      </button>
    </th>
  );
};

const applyDomainFilters = (rows, filters) => {
  const maxPrice = filters.maxPrice === "" ? null : Number(filters.maxPrice);
  return rows.filter((row) => {
    if (filters.extension && row.extension !== filters.extension) return false;
    if (filters.style && (row.style || "") !== filters.style) return false;
    if (filters.flavor && (row.flavor || "") !== filters.flavor) return false;
    if (filters.hidePremium && row.premium) return false;
    if (maxPrice != null && !Number.isNaN(maxPrice)) {
      if (row.price == null || Number(row.price) > maxPrice) return false;
    }
    return true;
  });
};

const DomainFilterPopover = ({
  filters,
  extensionOptions = EXTENSIONS,
  styleOptions,
  flavorOptions,
  onChange,
  onClear,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const activeCount = [
    filters.extension,
    filters.style,
    filters.flavor,
    filters.maxPrice,
    filters.hidePremium ? "1" : "",
  ].filter((v) => v !== "" && v != null).length;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded-lg border border-theme-secondary px-3 py-1.5 text-xs font-medium transition ${
          open || activeCount > 0
            ? "border-theme-primary/40 bg-theme-secondary text-theme-text"
            : "text-theme-text opacity-70 hover:opacity-100"
        }`}
      >
        <FunnelIcon className="h-3.5 w-3.5" />
        Filter
        {activeCount > 0 ? <span className="opacity-70">({activeCount})</span> : null}
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1.5 w-64 rounded-xl border border-theme-secondary bg-theme-main p-3 shadow-xl">
          <div className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-theme-text opacity-50">
                Extension
              </span>
              <select
                value={filters.extension}
                onChange={(e) => onChange("extension", e.target.value)}
                className="w-full rounded-lg border border-theme-secondary bg-theme-main px-2.5 py-1.5 text-xs text-theme-text"
              >
                <option value="">All</option>
                {extensionOptions.map((ext) => (
                  <option key={ext} value={ext}>{ext}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-theme-text opacity-50">
                Style
              </span>
              <select
                value={filters.style}
                onChange={(e) => onChange("style", e.target.value)}
                className="w-full rounded-lg border border-theme-secondary bg-theme-main px-2.5 py-1.5 text-xs text-theme-text"
              >
                <option value="">All</option>
                {styleOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-theme-text opacity-50">
                Flavor
              </span>
              <select
                value={filters.flavor}
                onChange={(e) => onChange("flavor", e.target.value)}
                className="w-full rounded-lg border border-theme-secondary bg-theme-main px-2.5 py-1.5 text-xs text-theme-text"
              >
                <option value="">All</option>
                {flavorOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-theme-text opacity-50">
                Max price
              </span>
              <input
                type="number"
                value={filters.maxPrice}
                onChange={(e) => onChange("maxPrice", e.target.value)}
                placeholder="Any"
                className="w-full rounded-lg border border-theme-secondary bg-theme-main px-2.5 py-1.5 text-xs text-theme-text"
              />
            </label>
            <label className="flex items-center gap-2 text-xs text-theme-text">
              <input
                type="checkbox"
                checked={filters.hidePremium}
                onChange={(e) => onChange("hidePremium", e.target.checked)}
              />
              Hide premium
            </label>
          </div>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="mt-3 text-xs font-medium text-theme-primary"
            >
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const DomainResultsTable = ({ domains }) => {
  const [sortKey, setSortKey] = useState("score");
  const [sortDir, setSortDir] = useState("desc");
  const showPrice = domains.some((d) => d.price != null);

  const onSort = (column) => {
    if (sortKey === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(column);
      setSortDir(
        column === "domain" || column === "price" || column === "style" || column === "flavor"
          ? "asc"
          : "desc",
      );
    }
  };

  const rows = useMemo(() => {
    const list = [...domains];
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (sortKey === "domain" || sortKey === "style" || sortKey === "flavor" || sortKey === "why") {
        return dir * String(av || "").localeCompare(String(bv || ""));
      }
      const an = av == null ? (sortDir === "asc" ? Infinity : -Infinity) : Number(av);
      const bn = bv == null ? (sortDir === "asc" ? Infinity : -Infinity) : Number(bv);
      return dir * (an - bn);
    });
    return list;
  }, [domains, sortKey, sortDir]);

  if (!rows.length) {
    return (
      <p className="text-sm text-theme-text opacity-60">
        No domains match these filters.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-theme-secondary">
      <table className="w-max text-left text-sm">
        <thead className="border-b border-theme-secondary bg-theme-secondary/30">
          <tr>
            <SortHeader label="Score" column="score" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
            <SortHeader label="Domain" column="domain" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
            <SortHeader label="Style" column="style" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
            <SortHeader label="Flavor" column="flavor" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
            <SortHeader label="Why" column="why" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
            {showPrice && (
              <SortHeader label="Price" column="price" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
            )}
            <th className="px-3 py-2.5" />
          </tr>
        </thead>
        <tbody className="divide-y divide-theme-secondary/50">
          {rows.map((row) => (
            <tr key={row.domain} className="hover:bg-black hover:bg-opacity-[0.03]">
              <td className="whitespace-nowrap px-3 py-3 align-middle">
                <span className="text-sm font-semibold tabular-nums text-theme-primary">
                  {row.score ?? "—"}
                </span>
              </td>
              <td className="whitespace-nowrap px-3 py-3 align-middle">
                <div className="font-medium text-theme-text">{row.domain}</div>
                {row.premium ? (
                  <div className="mt-0.5 text-[11px] text-theme-text opacity-40">premium</div>
                ) : null}
              </td>
              <td className="whitespace-nowrap px-3 py-3 align-middle">
                {row.style ? (
                  <span className="inline-flex whitespace-nowrap rounded-md bg-theme-secondary px-1.5 py-0.5 text-[11px] text-theme-text opacity-70">
                    {row.style}
                  </span>
                ) : (
                  <span className="text-xs text-theme-text opacity-40">—</span>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-3 align-middle">
                {row.flavor ? (
                  <span className="inline-flex whitespace-nowrap rounded-md bg-theme-secondary px-1.5 py-0.5 text-[11px] text-theme-text opacity-70">
                    {row.flavor}
                  </span>
                ) : (
                  <span className="text-xs text-theme-text opacity-40">—</span>
                )}
              </td>
              <td className="max-w-md px-3 py-3 align-middle text-sm text-theme-text opacity-70">
                {row.why || "—"}
              </td>
              {showPrice && (
                <td className="whitespace-nowrap px-3 py-3 align-middle tabular-nums text-theme-text opacity-80">
                  {row.price != null ? `$${row.price}` : "—"}
                </td>
              )}
              <td className="whitespace-nowrap px-3 py-3 text-right align-middle">
                <a
                  href={row.register_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-lg border border-theme-secondary px-2.5 py-1.5 text-xs font-medium text-theme-text hover:border-theme-primary hover:text-theme-primary"
                >
                  Register
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 opacity-60" />
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DomainFinder = ({ currentUser }) => {
  const { runHash } = useParams();
  const {
    run,
    error,
    submitting,
    loading,
    start,
    retry,
    openRun,
    newRun,
    runsVersion,
    isActive,
  } = useToolRun(TOOL_DOMAIN_FINDER, runHash);
  const showBuildCost = Boolean(currentUser?.features?.build_cost);

  const [description, setDescription] = useState("");
  const [extensions, setExtensions] = useState([".com"]);
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherDraft, setOtherDraft] = useState("");
  const [styles, setStyles] = useState([]);
  const [includeKeyword, setIncludeKeyword] = useState("");
  const [maxLength, setMaxLength] = useState("");
  const [mustInclude, setMustInclude] = useState("");
  const [mustExclude, setMustExclude] = useState("");
  const [allowSpecialCharacters, setAllowSpecialCharacters] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const allDomains = run?.result?.domains || [];
  const showForm = !runHash;
  const customExtensions = extensions.filter((ext) => !EXTENSIONS.includes(ext));
  const extensionOptions = useMemo(() => {
    const set = new Set(EXTENSIONS);
    allDomains.forEach((row) => {
      if (row.extension) set.add(row.extension);
    });
    return [...set];
  }, [allDomains]);

  const styleOptions = useMemo(() => {
    const set = new Set();
    allDomains.forEach((row) => {
      if (row.style) set.add(row.style);
    });
    return [...set].sort();
  }, [allDomains]);

  const flavorOptions = useMemo(() => {
    const set = new Set();
    allDomains.forEach((row) => {
      if (row.flavor) set.add(row.flavor);
    });
    return [...set].sort();
  }, [allDomains]);

  const filtered = useMemo(
    () => applyDomainFilters(allDomains, filters),
    [allDomains, filters],
  );

  const reportTitle = truncateText(run?.input?.description) || "Domain Finder";
  const countLabel =
    filtered.length < allDomains.length
      ? `${filtered.length}/${allDomains.length}`
      : String(allDomains.length);

  useEffect(() => {
    setFilters(EMPTY_FILTERS);
  }, [runHash]);

  const toggleExt = (ext) => {
    setExtensions((prev) =>
      prev.includes(ext) ? prev.filter((e) => e !== ext) : [...prev, ext],
    );
  };

  const addOtherExt = () => {
    const ext = normalizeExt(otherDraft);
    setOtherDraft("");
    setOtherOpen(false);
    if (!ext) return;
    setExtensions((prev) => (prev.includes(ext) ? prev : [...prev, ext]));
  };

  const toggleStyle = (id) => {
    setStyles((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const onSubmit = (e) => {
    e.preventDefault();
    start({
      description,
      extensions: extensions.length ? extensions : [".com"],
      styles,
      include_keyword: includeKeyword,
      max_length: maxLength ? Number(maxLength) : null,
      must_include: mustInclude,
      must_exclude: mustExclude,
      allow_special_characters: allowSpecialCharacters,
    });
  };

  const onFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const main = (
    <div className="space-y-4">
      {error && <p className="text-sm text-red-600">{error}</p>}

      {showForm && (
        <>
          <p className="text-sm text-theme-text opacity-60">
            Describe your niche and get ranked available domains.
          </p>
          <form onSubmit={onSubmit} className="space-y-4 rounded-xl border border-theme-secondary p-4">
            <label className="block">
              <span className="text-sm font-medium text-theme-text">Description</span>
              <textarea
                required
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Reviews of robot pool cleaners for homeowners"
                className="mt-1 w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary"
              />
            </label>

            <div>
              <span className="text-sm font-medium text-theme-text">Extensions</span>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {EXTENSIONS.map((ext) => (
                  <label
                    key={ext}
                    className={`cursor-pointer rounded-lg border px-2.5 py-1 text-xs ${
                      extensions.includes(ext)
                        ? "border-theme-primary bg-theme-primary/10 text-theme-primary"
                        : "border-theme-secondary text-theme-text opacity-70"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={extensions.includes(ext)}
                      onChange={() => toggleExt(ext)}
                    />
                    {ext}
                  </label>
                ))}
                {customExtensions.map((ext) => (
                  <button
                    key={ext}
                    type="button"
                    onClick={() => toggleExt(ext)}
                    className="rounded-lg border border-theme-primary bg-theme-primary/10 px-2.5 py-1 text-xs text-theme-primary"
                    title="Click to remove"
                  >
                    {ext}
                  </button>
                ))}
                {otherOpen ? (
                  <input
                    autoFocus
                    value={otherDraft}
                    onChange={(e) => setOtherDraft(e.target.value)}
                    onBlur={addOtherExt}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addOtherExt();
                      }
                      if (e.key === "Escape") {
                        setOtherDraft("");
                        setOtherOpen(false);
                      }
                    }}
                    placeholder=".dev"
                    className="w-20 rounded-lg border border-theme-primary bg-theme-main px-2 py-1 text-xs text-theme-text focus:outline-none focus:ring-1 focus:ring-theme-primary"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setOtherOpen(true)}
                    className="rounded-lg border border-dashed border-theme-secondary px-2.5 py-1 text-xs text-theme-text opacity-60 hover:opacity-100"
                  >
                    + other
                  </button>
                )}
              </div>
            </div>

            <div>
              <span className="text-sm font-medium text-theme-text">Styles</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {STYLES.map((style) => (
                  <label
                    key={style.id}
                    className={`cursor-pointer rounded-lg border px-2.5 py-1 text-xs ${
                      styles.includes(style.id)
                        ? "border-theme-primary bg-theme-primary/10 text-theme-primary"
                        : "border-theme-secondary text-theme-text opacity-70"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={styles.includes(style.id)}
                      onChange={() => toggleStyle(style.id)}
                    />
                    {style.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="font-medium text-theme-text">Include keyword</span>
                <input
                  value={includeKeyword}
                  onChange={(e) => setIncludeKeyword(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-theme-text"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-theme-text">Max length</span>
                <input
                  type="number"
                  min={3}
                  max={63}
                  value={maxLength}
                  onChange={(e) => setMaxLength(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-theme-text"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-theme-text">Must include words</span>
                <input
                  value={mustInclude}
                  onChange={(e) => setMustInclude(e.target.value)}
                  placeholder="comma separated"
                  className="mt-1 w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-theme-text"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-theme-text">Exclude words</span>
                <input
                  value={mustExclude}
                  onChange={(e) => setMustExclude(e.target.value)}
                  placeholder="comma separated"
                  className="mt-1 w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-theme-text"
                />
              </label>
            </div>

            <label className="flex items-center gap-2 text-sm text-theme-text">
              <input
                type="checkbox"
                checked={allowSpecialCharacters}
                onChange={(e) => setAllowSpecialCharacters(e.target.checked)}
              />
              <span>
                Allow special characters
                <span className="ml-1 opacity-50">(hyphens and numbers)</span>
              </span>
            </label>

            <button
              type="submit"
              disabled={submitting || !description.trim()}
              className="rounded-lg bg-theme-primary px-4 py-2 text-sm font-medium text-theme-button disabled:opacity-40"
            >
              {submitting ? "Starting…" : "Find domains"}
            </button>
          </form>
        </>
      )}

      {runHash && loading && <RunLoading />}

      {runHash && !loading && (isActive || run?.status === "failed") && (
        <RunProgress
          run={run}
          showBuildCost={showBuildCost}
          onRetry={retry}
          retrying={submitting}
        />
      )}

      {run?.status === "succeeded" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <ReportTitle title={reportTitle} countLabel={countLabel} />
              {availabilityLabel(run?.result?.summary) && (
                <p className="mt-1 text-xs text-theme-text opacity-50">
                  {availabilityLabel(run.result.summary)}
                </p>
              )}
            </div>
            {allDomains.length > 0 && (
              <div className="flex shrink-0 items-center gap-2">
                <DomainFilterPopover
                  filters={filters}
                  extensionOptions={extensionOptions}
                  styleOptions={styleOptions}
                  flavorOptions={flavorOptions}
                  onChange={onFilterChange}
                  onClear={() => setFilters(EMPTY_FILTERS)}
                />
                <button
                  type="button"
                  onClick={() => downloadCsv("domain-finder.csv", filtered, CSV_COLUMNS)}
                  className="rounded-lg border border-theme-secondary px-3 py-1.5 text-xs font-medium text-theme-text opacity-70 hover:opacity-100"
                >
                  Export CSV
                </button>
              </div>
            )}
          </div>
          <DomainResultsTable domains={filtered} />
          <RunProgress run={run} showBuildCost={showBuildCost} />
        </div>
      )}
    </div>
  );

  return (
    <ToolTwoColumn
      main={main}
      side={(
        <PastRunsSidebar
          toolId={TOOL_DOMAIN_FINDER}
          activeHash={runHash}
          onOpen={openRun}
          onNew={newRun}
          refreshKey={runsVersion}
        />
      )}
    />
  );
};

export default DomainFinder;
